import { ChangeDetectionStrategy, Component, forwardRef, input, output, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TSIconModule } from '../ts-icon/ts-icon.module';

// Variants share the ts-button palette: the selected segment is filled with the variant colour.
export type TsButtonToggleVariant = 'primary' | 'secondary' | 'danger';

export interface TsButtonToggleOption<T = string> {
  value: T;
  label: string;
  icon?: string;
  disabled?: boolean;
}

/**
 * A segmented single-select toggle. Bind it like any reactive-forms control (`[formControl]` /
 * `formControlName` / `ngModel`) — it implements `ControlValueAccessor` — or listen to `(change)`.
 */
@Component({
  selector: 'ts-button-toggle',
  standalone: true,
  imports: [NgClass, TSIconModule],
  templateUrl: './ts-button-toggle.component.html',
  styleUrl: './ts-button-toggle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TsButtonToggleComponent),
      multi: true,
    },
  ],
  host: {
    role: 'group',
    '[attr.aria-label]': 'ariaLabel()',
  },
})
export class TsButtonToggleComponent<T = string> implements ControlValueAccessor {
  readonly options = input<TsButtonToggleOption<T>[]>([]);
  readonly variant = input<TsButtonToggleVariant>('primary');
  readonly ariaLabel = input<string | undefined>(undefined, { alias: 'aria-label' });

  readonly value = signal<T | null>(null);
  readonly isDisabled = signal(false);

  readonly change = output<T>();

  private onChange: (value: T) => void = () => {};
  private onTouched: () => void = () => {};

  isSelected(option: TsButtonToggleOption<T>): boolean {
    return Object.is(option.value, this.value());
  }

  select(option: TsButtonToggleOption<T>): void {
    if (this.isDisabled() || option.disabled || this.isSelected(option)) {
      return;
    }

    this.value.set(option.value);
    this.onChange(option.value);
    this.onTouched();
    this.change.emit(option.value);
  }

  writeValue(value: T | null): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: T) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }
}
